import useData from 'hooks/useData';
import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';
import { CoworkerGridDataRef, GridData, OrgSelection, TimeSelection } from 'types/appContext';
import type { UserProfile } from 'types/authContext';
import type { Scenario } from 'types/scenario';
import type { Coworker } from 'types/coworker';
import { updateCoworkerGridDataRef } from './gridFunctions';
import { generateGridData } from './gridFunctionsMain';

const useGrid = (
    {
        coworkers,
        timeSelection,
        currentScenario,
        user,
        orgSelection,
        setIsCalculating,
    }: {
        coworkers: Array<Coworker> | undefined,
        timeSelection: TimeSelection,
        currentScenario: Scenario | undefined,
        user: UserProfile | null,
        orgSelection: OrgSelection,
        setIsCalculating: Dispatch<SetStateAction<boolean>>,
    }
) => {
    const coworkerGridDataRef = useRef<CoworkerGridDataRef | undefined>(undefined);
    const [gridData, setGridData] = useState<GridData | undefined>(undefined);
    const {
        currentUnitRates,
        currentUnitBudget,
        defaultContributions,
        customContributions,
        workload,
        orgData,
        currentUnit,
        currentUnitType,
        config,
        modulationData,
    } = useData();
    //* Remove duplicate costCentreCode entries from orgSelection due to OPP OM API
    const uniqueSelectedCostCentres = orgSelection.utils.selectedCostCentres
        .filter((cc, index, self) => index === self.findIndex(other => other.costCentreCode === cc.costCentreCode));

    useEffect(() => {
        setIsCalculating(true);

        // Using setTimeout to push the calculation to the next event loop, so the loading screen can be shown
        setTimeout(() => {
            coworkerGridDataRef.current = updateCoworkerGridDataRef({
                coworkerGrid: coworkerGridDataRef.current,
                coworkers,
                timeSelection,
                user,
                currentUnit,
                currentUnitType,
                orgData,
                currentUnitRates,
                defaultContributions,
                customContributions,
                modulationData,
            });

            setGridData(() => generateGridData({
                config,
                selectedCostCentres: uniqueSelectedCostCentres,
                storedNoScenarioData: coworkerGridDataRef.current,
                coworkers,
                currentScenario,
                currentUnit,
                currentUnitType,
                currentUnitBudget,
                currentUnitRates,
                customContributions,
                defaultContributions,
                timeSelection,
                orgData,
                user,
                workload,
                modulationData,
                orgSelection
            }));
            setIsCalculating(false);
        }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        coworkers,
        config,
        user,
        timeSelection,
        currentUnitRates,
        defaultContributions,
        customContributions,
        currentUnitBudget,
        currentUnit,
        currentUnitType,
        setIsCalculating,
        currentScenario,
        orgSelection,
        workload,
        orgData,
        modulationData,
    ]);

    return {
        gridData,
    };
};

export default useGrid;
